import type { Opportunity } from "../types";

export const COMMERCIAL_OPEN_STAGES = Object.freeze([
  "discovery",
  "qualified",
  "proposal",
  "decision",
] as const);

export type OpportunityLifecycleState =
  | "NO_OPPORTUNITY"
  | "OPPORTUNITY_AVAILABLE"
  | "OPPORTUNITY_BEING_CREATED"
  | "OPEN_OPPORTUNITY"
  | "CLOSED_WON"
  | "CLOSED_LOST";

export type OpportunityLifecycle = {
  state: OpportunityLifecycleState;
  label: string;
  detail: string;
  boundOpportunity: Opportunity | null;
  openOpportunities: Opportunity[];
};

export function opportunitiesForOrganization(
  organizationId: string | null | undefined,
  opportunities: Opportunity[],
  ensuredOpportunity: Opportunity | null,
): Opportunity[] {
  if (!organizationId) return [];
  const canonical = opportunities.filter(
    (opportunity) => opportunity.organizationId === organizationId,
  );
  if (
    !ensuredOpportunity ||
    ensuredOpportunity.organizationId !== organizationId ||
    canonical.some((opportunity) => opportunity.id === ensuredOpportunity.id)
  ) {
    return canonical;
  }
  return [ensuredOpportunity, ...canonical];
}

export function resolveOpportunityLifecycle(
  opportunities: Opportunity[],
  selectedOpportunityId: string,
  creating: boolean,
): OpportunityLifecycle {
  const openOpportunities = opportunities.filter((opportunity) =>
    (COMMERCIAL_OPEN_STAGES as readonly string[]).includes(opportunity.stage),
  );
  const selected = openOpportunities.find(
    (opportunity) => opportunity.id === selectedOpportunityId,
  );
  const boundOpportunity =
    selected ?? (openOpportunities.length === 1 ? openOpportunities[0] : null);

  if (creating) {
    return {
      state: "OPPORTUNITY_BEING_CREATED",
      label: "Creating opportunity",
      detail: "Confirming one canonical Discovery opportunity for this close.",
      boundOpportunity: null,
      openOpportunities,
    };
  }
  if (openOpportunities.length > 1 && !boundOpportunity) {
    return {
      state: "OPPORTUNITY_AVAILABLE",
      label: "Opportunity available",
      detail: "Choose the open opportunity that belongs to this close.",
      boundOpportunity: null,
      openOpportunities,
    };
  }
  if (boundOpportunity) {
    return {
      state: "OPEN_OPPORTUNITY",
      label: `Open · ${boundOpportunity.stage}`,
      detail: boundOpportunity.name,
      boundOpportunity,
      openOpportunities,
    };
  }

  const latestClosed = opportunities
    .filter((opportunity) =>
      opportunity.stage === "won" || opportunity.stage === "lost",
    )
    .sort(
      (left, right) =>
        Date.parse(right.updatedAt) - Date.parse(left.updatedAt),
    )[0];
  if (latestClosed?.stage === "won") {
    return {
      state: "CLOSED_WON",
      label: "Closed · Won",
      detail: `${latestClosed.name} is closed won. Create a new opportunity only for a new sale.`,
      boundOpportunity: null,
      openOpportunities,
    };
  }
  if (latestClosed?.stage === "lost") {
    return {
      state: "CLOSED_LOST",
      label: "Closed · Lost",
      detail: `${latestClosed.name} is closed lost. Create a new opportunity to reopen a new close workflow.`,
      boundOpportunity: null,
      openOpportunities,
    };
  }
  return {
    state: "NO_OPPORTUNITY",
    label: "No opportunity",
    detail: "Create one Discovery opportunity before configuring this close.",
    boundOpportunity: null,
    openOpportunities,
  };
}

export type CommercialReadiness = {
  ready: boolean;
  label: "READY_TO_CLOSE" | "ACTION_REQUIRED" | "CALCULATING";
  requirements: string[];
};

export function resolveCommercialReadiness(input: {
  hasContact: boolean;
  hasOrganization: boolean;
  hasOpportunity: boolean;
  inputsValid: boolean;
  quoteLoading: boolean;
  hasQuote: boolean;
  quoteError: boolean;
  approvalRequired: boolean;
  approvalStatus: string | null;
  termsDrift: boolean;
  hasReachableEmail: boolean;
}): CommercialReadiness {
  const requirements: string[] = [];
  if (!input.hasContact) requirements.push("Select a canonical contact");
  if (input.hasContact && !input.hasOrganization)
    requirements.push("Link the contact to an organization");
  if (input.hasOrganization && !input.hasOpportunity)
    requirements.push("Create or choose an open opportunity");
  if (!input.inputsValid) requirements.push("Confirm plan and locations");
  if (input.quoteError) requirements.push("Resolve the pricing error");
  if (!input.quoteLoading && input.inputsValid && !input.hasQuote)
    requirements.push("Confirm authoritative pricing");
  if (input.approvalRequired && input.approvalStatus !== "APPROVED")
    requirements.push(
      input.approvalStatus === "PENDING_APPROVAL"
        ? "Wait for discount approval"
        : "Obtain discount approval",
    );
  if (input.termsDrift)
    requirements.push("Request approval for the current terms");
  if (input.hasContact && !input.hasReachableEmail)
    requirements.push("Add a reachable contact email");

  if (input.quoteLoading) {
    return { ready: false, label: "CALCULATING", requirements };
  }
  return {
    ready: requirements.length === 0,
    label: requirements.length === 0 ? "READY_TO_CLOSE" : "ACTION_REQUIRED",
    requirements,
  };
}
